import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/veteran-facing-services-tools/veteran-facing-services-tools/packages/documentation/src/layouts/SidebarLayout.jsx";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "adding-a-collection-of-related-links-for-markdown-pages"
    }}>{`Adding a collection of related links for Markdown pages`}</h1>
    <div className="deprecation-message">
    <h2>We're moving our docs!</h2>
    <h3>Find <a href="https://depo-platform-documentation.scrollhelp.site/developer-docs/Adding-a-collection-of-related-links-for-markdown-pages.1854996617.html">the latest version of this page</a> on the Platform website.</h3>
    <h3>Still can't find what you're looking for? Reach out to <a href="https://dsva.slack.com/archives/CBU0KDSB1">#vfs-platform-support</a> on Slack.</h3>
    </div>
    <p>{`Our current publishing system, Metalsmith, provides a way to group related content using what it calls `}<em parentName="p">{`collections`}</em>{`. Using collections requires the `}<inlineCode parentName="p">{`metalsmith-collections`}</inlineCode>{` plugin (`}<a parentName="p" {...{
        "href": "https://github.com/segmentio/metalsmith-collections"
      }}>{`documentation`}</a>{`).`}</p>
    <p>{`Content grouped within a collection is displayed in the sidebar navigation, and the next/previous links. Next and previous links are set based on the sorting order (`}<inlineCode parentName="p">{`sortBy`}</inlineCode>{` property of the collection; see below)`}</p>
    <p>{`Viewing `}<inlineCode parentName="p">{`src/site/components/navigation-sidebar.html`}</inlineCode>{` and `}<inlineCode parentName="p">{`src/site/components/navigation-next-previous.html`}</inlineCode>{` `}<em parentName="p">{`may`}</em>{` clarify some of what's in this document.`}</p>
    <h2 {...{
      "id": "add-a-new-collection"
    }}>{`Add a new collection`}</h2>
    <p><em parentName="p"><strong parentName="em">{`NOTE`}</strong>{`: Content team, you may wish to file a request with the DevOps team for this task.`}</em></p>
    <p>{`To create a new collection, add a new collection object to `}<inlineCode parentName="p">{`vets-website/script/build.js`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`  smith.use(collections({
    collectionName: {
      pattern: 'path/to/the/files/*.md',
      sortBy: 'order',
      metadata: {
        name: 'Title for collection 1'
      }
    },
    secondCollectionName: {
      pattern: 'path/to/the/files/*.md',
      sortBy: 'order',
      metadata: {
        name: 'Title for collection two'
      }
    }
  }));
`}</code></pre>
    <h3 {...{
      "id": "what-each-field-means"
    }}>{`What each field means:`}</h3>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`collectionName`}</inlineCode>{`: should be a camel-cased or snake cased string that summarizes the name of the related documents.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`pattern`}</inlineCode>{`: should be a directory a relative to the site root (without a leading slash), and end with `}<inlineCode parentName="p">{`*.md`}</inlineCode>{` or `}<inlineCode parentName="p">{`*.html`}</inlineCode>{` (`}<inlineCode parentName="p">{`*`}</inlineCode>{` is a `}<em parentName="p">{`wildcard`}</em>{` character that will match every file with an `}<inlineCode parentName="p">{`.md`}</inlineCode>{` or `}<inlineCode parentName="p">{`*.html`}</inlineCode>{` extension.)`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`sortBy`}</inlineCode>{`: is any current YAML property used in the front matter metadata (front matter data is the stuff between the `}<inlineCode parentName="p">{`---`}</inlineCode>{` at the beginning of each content file). This should be:`}</p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`order`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`title`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`display_title`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`metadata`}</inlineCode>{`: must be a JavaScript object that contains a `}<inlineCode parentName="p">{`name`}</inlineCode>{` property. This field is optional. You may add additional properties as here.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "adding-files-to-a-collection-even-when-it-is-not-in-the-collection-path"
    }}>{`Adding files to a collection even when it is not in the collection path`}</h3>
    <p>{`Collections can also be defined or augmented by adding a `}<inlineCode parentName="p">{`collection`}</inlineCode>{` property to front matter fields. For example:`}</p>
    <pre><code parentName="pre" {...{}}>{`---
layout: page-breadcrumbs.html
title: Agent Orange
plainlanguage: 10-21-16 certified in compliance with the Plain Writing Act
template: detail-page
collection: disabilityExposureHazMat
---
`}</code></pre>
    <p>{`In general you should:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Use a collection objects to configure collections.`}</strong>{` Items within a collection directory will be added to the collection automatically`}</li>
      <li parentName="ul"><strong parentName="li">{`Add a collection property to include an index or other file`}</strong>{` in the collection. For example, `}<inlineCode parentName="li">{`exposure-to-hazardous-materials/agent-orange.md`}</inlineCode>{` is also an index page for several Agent Orange-related disability pages and should have a `}<inlineCode parentName="li">{`disabilityExposureHazMat`}</inlineCode>{` property.`}</li>
    </ul>
    <p><strong parentName="p">{`NOTE:`}</strong>{` Adding a `}<inlineCode parentName="p">{`collection`}</inlineCode>{` property in YAML will not override a path-based collection value. You'll actually need to move the file.`}</p>
    <h2 {...{
      "id": "ordering-pages-within-a-collection"
    }}>{`Ordering pages within a collection`}</h2>
    <p>{`When defining a collection, you may choose to sort pages by date, or by title. In many cases, however, the desired page order may not use either of those fields.`}</p>
    <p>{`To control the order of display within a collection, add an `}<inlineCode parentName="p">{`order`}</inlineCode>{` property to the front matter of each page.`}</p>
    <p>{`The value of `}<inlineCode parentName="p">{`order`}</inlineCode>{` should be a number. Pages will be ordered in ascending order.`}</p>
    <h2 {...{
      "id": "adding-a-child-collection"
    }}>{`Adding a child collection`}</h2>
    <p>{`Collections do not have a hierarchy by default. Create one by adding a `}<inlineCode parentName="p">{`children`}</inlineCode>{` property to the parent page or index page of a child collection.`}</p>
    <pre><code parentName="pre" {...{}}>{`---
layout: page-breadcrumbs.html
title: Agent Orange
plainlanguage: 10-21-16 certified in compliance with the Plain Writing Act
template: detail-page
collection: disabilityExposureHazMat
children: disabilityAgentOrange
order: 1
---
`}</code></pre>
    <p><inlineCode parentName="p">{`children`}</inlineCode>{` should be the identifier for the child collection.`}</p>
    <h2 {...{
      "id": "current-collections-and-hierarchy"
    }}>{`Current collections and hierarchy`}</h2>
    <p><em parentName="p">{`See vets-website/script/build.js`}</em>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Label`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Path`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Child collection(s)`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`disabilityExposureHazMat`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`disability-benefits/conditions/exposure-to-hazardous-materials/*.md`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`disabilityAgentOrange`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`disabilityAgentOrange`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`disability-benefits/conditions/exposure-to-hazardous-materials/agent-orange/*.md`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`–`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`education`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`education/*.md`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`educationGIBill`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`educationGIBill`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`education/gi-bill/*.md`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`–`}</td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      